import React from "react";
import { useCC, scopeParams } from "../lib/ccClient";
import { Panel, MetricGrid, AsyncPanelBody, EvidenceBadge } from "../components/primitives";
import { formatMetric } from "../lib/evidence";
import { FileCheck2, ShieldAlert, BellRing, Plug, Info, PlugZap } from "lucide-react";

// Turn a {key: metric} object into [{label, metric}] using a friendly label map.
const toMetrics = (data, labels) =>
  Object.entries(labels)
    .filter(([k]) => data && data[k])
    .map(([k, label]) => ({ label, metric: data[k] }));

// Truthful connectivity banner — surfaces the exact provider blocker when a real source
// is not connected/configured (never fabricates a value in its place).
const ConnectivityNote = ({ conn }) => {
  if (!conn) return null;
  const ok = conn.state === "VERIFIED" && !conn.blocker;
  return (
    <div data-testid="cc-connectivity" className={`flex items-start gap-2 mb-4 px-3 py-2 rounded-lg border text-xs ${ok ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-300" : "bg-zinc-500/5 border-white/10 text-zinc-300"}`}>
      {ok ? <PlugZap className="w-4 h-4 shrink-0 mt-0.5 text-emerald-400" /> : <Info className="w-4 h-4 shrink-0 mt-0.5 text-zinc-400" />}
      <div>
        <span className="font-bold">{conn.provider}: {conn.state}</span>
        {conn.blocker && <span className="block text-zinc-400 mt-0.5">{conn.blocker}</span>}
        {conn.requiredCredential && <span className="block text-zinc-500 mt-0.5">Requires: {conn.requiredCredential}</span>}
        {conn.window && <span className="block text-zinc-500 mt-0.5">Window: last {conn.window.days}d since {conn.window.since}{conn.window.partial ? " (partial)" : ""}</span>}
      </div>
    </div>
  );
};

const SUMMARY_LABELS = {
  marketplaces: "Marketplaces", activeMarketplaces: "Active Marketplaces",
  sellerOrganizations: "Seller Organizations", activeSellers: "Active Sellers",
  stores: "Stores", activeStores: "Active Stores", customers: "Customers",
  activeProducts: "Active Products", pendingProducts: "Pending Products", blockedProducts: "Blocked Products",
};
const COMMERCE_LABELS = {
  gmv: "GMV", grossRevenue: "Gross Revenue", netRevenue: "Net Revenue", orders: "Orders",
  averageOrderValue: "Avg Order Value", refunds: "Refunds", returns: "Returns",
  chargebacks: "Chargebacks", conversionRate: "Conversion Rate",
};
const FINANCIAL_LABELS = {
  subscriptionRevenueMRR: "Subscription MRR", activeSubscriptions: "Active Subscriptions",
  customerPayments: "Customer Payments", platformFees: "Platform Fees", sellerPayable: "Seller Payable",
  pendingSettlements: "Pending Settlements", paidSettlements: "Paid Settlements", refundExposure: "Refund Exposure",
  paymentProcessingFees: "Processing Fees", taxes: "Taxes", shippingNet: "Shipping Net",
  advertisingRevenue: "Ad Revenue",
};
const INVENTORY_LABELS = {
  outOfStock: "Out of Stock", lowStock: "Low Stock", oversold: "Oversold", syncFailures: "Sync Failures",
  supplierConflicts: "Supplier Conflicts", productsWithoutSupplier: "No Supplier", staleInventory: "Stale Inventory",
};
const PRODUCT_LABELS = {
  awaitingReview: "Awaiting Review", restricted: "Restricted", duplicates: "Duplicates",
  trademarkRisk: "Trademark Risk", missingImages: "Missing Images", missingDescriptions: "Missing Descriptions",
  unprofitable: "Unprofitable", priceProtectionFailures: "Price Protection Fails", supplierCostChanges: "Cost Changes",
};

const QUICK_ACTIONS = [
  { id: "applications", label: "Review Applications", icon: FileCheck2, target: "applications", kind: "admin" },
  { id: "needsreview", label: "Needs Review", icon: ShieldAlert, target: "needsreview", kind: "admin" },
  { id: "alerts", label: "Alert Center", icon: BellRing, target: "alerts", kind: "panel" },
  { id: "connectors", label: "Connector Health", icon: Plug, target: "connectors", kind: "panel" },
];

export function OverviewPanel({ scope, range, onNavigate, onOpenAdmin }) {
  const params = scopeParams(scope, range);
  const s = useCC("/summary", params);
  const c = useCC("/commerce", params);
  return (
    <div className="space-y-6">
      <Panel testid="cc-panel-summary" title="Platform Summary"
        subtitle="Ecosystem footprint — verified counts, truthful NOT_CONNECTED where no source exists"
        actions={s.meta?.freshness?.primary?.state && <EvidenceBadge state={s.meta.freshness.primary.state} />}>
        <AsyncPanelBody loading={s.loading} error={s.error} onRetry={s.refetch}>
          <MetricGrid metrics={toMetrics(s.data, SUMMARY_LABELS)} />
        </AsyncPanelBody>
      </Panel>

      <Panel testid="cc-panel-commerce-kpi" title="Commerce KPIs"
        subtitle="Order & revenue pipeline"
        actions={c.meta?.freshness?.primary?.state && <EvidenceBadge state={c.meta.freshness.primary.state} />}>
        <AsyncPanelBody loading={c.loading} error={c.error} onRetry={c.refetch}>
          <ConnectivityNote conn={c.data?._connectivity} />
          <MetricGrid metrics={toMetrics(c.data, COMMERCE_LABELS)} />
        </AsyncPanelBody>
      </Panel>

      <Panel testid="cc-panel-quick-actions" title="Quick Actions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {QUICK_ACTIONS.map((a) => (
            <button key={a.id} data-testid={`cc-quick-${a.id}`}
              onClick={() => (a.kind === "admin" ? onOpenAdmin(a.target) : onNavigate(a.target))}
              className="flex items-center gap-2.5 bg-[#141a28] border border-white/10 rounded-lg px-4 py-3 text-left hover:border-[#C5A059]/50 transition-colors">
              <a.icon className="w-4 h-4 text-[#C5A059]" />
              <span className="text-xs font-bold text-white">{a.label}</span>
            </button>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const SimpleMetricPanel = ({ endpoint, title, subtitle, labels, testid, scope, range }) => {
  const params = scopeParams(scope, range);
  const { loading, error, data, meta, refetch } = useCC(endpoint, params);
  return (
    <Panel testid={testid} title={title} subtitle={subtitle}
      actions={meta?.freshness?.primary?.state && <EvidenceBadge state={meta.freshness.primary.state} />}>
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        {(endpoint === "/commerce" || endpoint === "/financial") && <ConnectivityNote conn={data?._connectivity} />}
        <MetricGrid metrics={toMetrics(data, labels)} />
      </AsyncPanelBody>
    </Panel>
  );
};

export const CommercePanel = (p) => (
  <SimpleMetricPanel {...p} endpoint="/commerce" title="Commerce KPIs"
    subtitle="Order & revenue pipeline — Shopify" labels={COMMERCE_LABELS} testid="cc-panel-commerce" />
);
export const FinancialPanel = (p) => (
  <SimpleMetricPanel {...p} endpoint="/financial" title="Financial"
    subtitle="Ledger & settlements (integer minor-units)" labels={FINANCIAL_LABELS} testid="cc-panel-financial" />
);
export const InventoryPanel = (p) => (
  <SimpleMetricPanel {...p} endpoint="/inventory" title="Inventory Control"
    subtitle="Supplier stock signals" labels={INVENTORY_LABELS} testid="cc-panel-inventory" />
);
export const ProductsPanel = (p) => (
  <SimpleMetricPanel {...p} endpoint="/products" title="Product Control"
    subtitle="Catalog governance" labels={PRODUCT_LABELS} testid="cc-panel-products" />
);

export function OrdersPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, meta, refetch } = useCC("/orders", params);
  const dist = data?.distribution || {};
  return (
    <div className="space-y-6">
      <Panel testid="cc-panel-orders" title="Order Distribution"
        subtitle="Lifecycle status breakdown"
        actions={meta?.freshness?.primary?.state && <EvidenceBadge state={meta.freshness.primary.state} />}>
        <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(dist).map(([status, m]) => {
              const f = formatMetric(m);
              return (
                <div key={status} data-testid={`cc-order-status-${status}`}
                  className="bg-[#141a28] border border-white/10 rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 capitalize">{status}</p>
                  <p className={`text-xl font-black mt-1 ${f.isValue ? "text-white" : "text-zinc-500"}`}>
                    {f.isValue ? f.display : <span className="text-xs">{f.display}</span>}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            <MetricGridInline label="Fulfillment Delay" m={data?.fulfillmentDelay} />
            <MetricGridInline label="Orders at Risk" m={data?.ordersAtRisk} />
          </div>
        </AsyncPanelBody>
      </Panel>
    </div>
  );
}

const MetricGridInline = ({ label, m }) => {
  const f = formatMetric(m);
  return (
    <div className="bg-[#141a28] border border-white/10 rounded-lg p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
      <p className={`text-xl font-black mt-1 ${f.isValue ? "text-white" : "text-zinc-500"}`}>
        {f.isValue ? f.display : <span className="text-xs">{f.display}</span>}
      </p>
    </div>
  );
};
